import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Row,
  Col,
  Card,
  Input,
  InputNumber,
  Select,
  Button,
  Table,
  Tag,
  Typography,
  Space,
  Modal,
  message,
  Badge,
  Alert,
  Spin,
  Empty,
  Divider,
  Statistic,
  Checkbox,
  Tooltip,
} from "antd";
import {
  ScanOutlined,
  CameraOutlined,
  SearchOutlined,
  PlusOutlined,
  MinusOutlined,
  DeleteOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  MoneyCollectOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import { useQuery, useQueryClient } from "react-query";
import { Html5Qrcode } from "html5-qrcode";
import api from "../services/api";
import usePosStore from "../stores/posStore";

const { Text, Title } = Typography;
const { Option } = Select;

const formatMoney = (value) => `NPR ${Number(value || 0).toFixed(2)}`;

const normalizeProduct = (p) => ({
  id: p.id,
  name: p.name,
  price: parseFloat(p.price) || 0,
  sku: p.sku || null,
  barcode: p.barcode || null,
  stock_quantity:
    typeof p.stock_quantity === "number" ? p.stock_quantity : null,
  type: p.type || "product",
  category_id: p.category_id ?? p.category?.id ?? null,
  category_name:
    typeof p.category === "string" ? p.category : p.category?.name || null,
  unit: typeof p.unit === "string" ? p.unit : p.unit?.name || null,
  is_available: p.is_available !== false,
});

const POS = () => {
  const queryClient = useQueryClient();
  const {
    cart,
    customerId,
    paymentMethod,
    billingType,
    vatRate,
    queuedSales,
    syncing,
    addToCart,
    updateCartItem,
    removeFromCart,
    setCheckoutField,
    completeSale,
    syncQueue,
    refreshQueuedCount,
  } = usePosStore();

  const [scanInput, setScanInput] = useState("");
  const [scanning, setScanning] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(undefined);
  const [cashReceived, setCashReceived] = useState(undefined);
  const [isOnline, setIsOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  const [cameraOpen, setCameraOpen] = useState(false);
  const [billToo, setBillToo] = useState(true);

  const scanInputRef = useRef(null);
  const cameraRef = useRef(null);
  const lastScanRef = useRef({ text: "", time: 0 });

  // ---- Data ---------------------------------------------------------------
  const { data: products = [], isLoading: productsLoading } = useQuery(
    "pos-products",
    async () => {
      const response = await api.get("/admin/inventory/products?per_page=-1");
      const list = response.data?.data ?? response.data ?? [];
      return list.map(normalizeProduct);
    },
    { retry: 1, staleTime: 60 * 1000 },
  );

  const { data: customers = [] } = useQuery(
    "pos-customers",
    async () => {
      const response = await api.get("/admin/users/list");
      return response.data?.data ?? response.data?.users ?? response.data ?? [];
    },
    { retry: 1, staleTime: 5 * 60 * 1000 },
  );

  const categories = useMemo(() => {
    const map = new Map();
    products.forEach((p) => {
      if (p.category_id && p.category_name) {
        map.set(p.category_id, p.category_name);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [products]);

  const filteredProducts = useMemo(() => {
    const term = catalogSearch.trim().toLowerCase();
    return products.filter((p) => {
      if (p.type !== "product") return false;
      if (categoryFilter && p.category_id !== categoryFilter) return false;
      if (!term) return true;
      return (
        p.name.toLowerCase().includes(term) ||
        (p.barcode && String(p.barcode).toLowerCase().includes(term)) ||
        (p.sku && String(p.sku).toLowerCase().includes(term))
      );
    });
  }, [products, catalogSearch, categoryFilter]);

  // ---- Barcode add --------------------------------------------------------
  const addByBarcode = useCallback(
    async (code) => {
      const trimmed = (code || "").toString().trim();
      if (!trimmed) return;
      setScanning(true);
      try {
        const response = await api.get(
          `/admin/products/barcode/${encodeURIComponent(trimmed)}`,
        );
        const product = response.data?.data;
        if (!product) {
          message.warning("Product not found for this barcode");
          return;
        }
        addToCart({
          id: product.id,
          name: product.name,
          type: product.type,
          sku: product.sku,
          barcode: product.barcode,
          price: parseFloat(product.price) || 0,
          stock_quantity: product.stock_quantity,
        });
        message.success(`${product.name} added`);
      } catch (error) {
        if (error.response?.status === 404) {
          message.warning("Product not found for this barcode");
        } else {
          message.error(
            error.response?.data?.message || "Failed to look up barcode",
          );
        }
      } finally {
        setScanning(false);
        setTimeout(() => scanInputRef.current?.focus(), 0);
      }
    },
    [addToCart],
  );

  const handleScanSubmit = () => {
    if (!scanInput.trim()) return;
    addByBarcode(scanInput);
    setScanInput("");
  };

  // ---- Camera scanning ----------------------------------------------------
  useEffect(() => {
    if (!cameraOpen) return;
    let stopped = false;
    const scanner = new Html5Qrcode("pos-camera-scanner", { verbose: false });
    cameraRef.current = scanner;

    const handleDecoded = (decodedText) => {
      const now = Date.now();
      const last = lastScanRef.current;
      if (last.text === decodedText && now - last.time < 1500) return;
      lastScanRef.current = { text: decodedText, time: now };
      message.success(`Scanned: ${decodedText}`);
      scanner
        .stop()
        .then(() => scanner.clear())
        .catch(() => {});
      cameraRef.current = null;
      setCameraOpen(false);
      addByBarcode(decodedText);
    };

    scanner
      .start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 160 } },
        handleDecoded,
      )
      .catch((err) => {
        if (stopped) return;
        message.error(
          `Camera could not be started: ${String(err).slice(0, 120)}`,
        );
        setCameraOpen(false);
      });

    return () => {
      stopped = true;
      if (cameraRef.current) {
        cameraRef.current
          .stop()
          .then(() => cameraRef.current?.clear())
          .catch(() => {});
        cameraRef.current = null;
      }
    };
  }, [cameraOpen, addByBarcode]);

  // ---- Online / offline ---------------------------------------------------
  useEffect(() => {
    refreshQueuedCount();
    const handleOnline = () => {
      setIsOnline(true);
      message.success("Back online — syncing queued sales…");
      syncQueue();
    };
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [refreshQueuedCount, syncQueue]);

  // ---- Totals (VAT-inclusive to match backend) -----------------------------
  const subtotal = cart.reduce((sum, item) => sum + (item.total || 0), 0);
  const isVat = billingType === "vat" || billingType === "vat_all";
  const vatAmount = isVat
    ? subtotal - subtotal / (1 + (vatRate || 0) / 100)
    : 0;
  const total = isVat ? subtotal : subtotal;
  const change = Math.max(0, (cashReceived || 0) - total);

  // ---- Complete sale ------------------------------------------------------
  const handleComplete = async () => {
    if (cart.length === 0) {
      message.warning("Cart is empty");
      return;
    }
    const result = await completeSale({ billToo });
    queryClient.invalidateQueries("pos-products");
    queryClient.invalidateQueries("offline-sales");

    if (!result.ok) {
      message.error(result.error);
      return;
    }

    setCashReceived(undefined);
    if (result.queued) {
      message.success(
        "Sale saved offline. It will sync when you are back online.",
      );
    } else if (result.billError) {
      message.success(
        `Sale ${result.sale?.sale_number || ""} recorded. ${result.billError}`,
      );
    } else {
      const invoiceNumber =
        result.invoice?.invoice_number || result.invoice?.invoiceNumber || "";
      Modal.success({
        title: "Sale completed",
        content: `Offline sale recorded and invoice ${invoiceNumber || ""} generated.`,
      });
    }
    setTimeout(() => scanInputRef.current?.focus(), 0);
  };

  const handleSync = async () => {
    const result = await syncQueue();
    if (result.synced.length)
      message.success(`Synced ${result.synced.length} sale(s)`);
    if (result.failed.length) {
      message.warning(
        `${result.failed.length} sale(s) failed — ${result.failed[0].error}`,
      );
    }
  };

  // ---- Cart table columns -------------------------------------------------
  const cartColumns = [
    {
      title: "Product",
      dataIndex: "name",
      key: "name",
      render: (name, item) => (
        <div>
          <div style={{ fontWeight: 600 }}>{name}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {item.barcode || item.sku || "—"}
          </Text>
        </div>
      ),
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: 120,
      render: (qty, item) => (
        <Space size={4}>
          <Button
            size="small"
            icon={<MinusOutlined />}
            disabled={qty <= 1}
            onClick={() =>
              updateCartItem(item.product_id, { quantity: qty - 1 })
            }
          />
          <InputNumber
            size="small"
            value={qty}
            min={1}
            max={item.stock}
            style={{ width: 52 }}
            onChange={(v) =>
              updateCartItem(item.product_id, { quantity: v || 1 })
            }
          />
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={() =>
              updateCartItem(item.product_id, { quantity: qty + 1 })
            }
          />
        </Space>
      ),
    },
    {
      title: "Price",
      dataIndex: "unit_price",
      key: "unit_price",
      width: 120,
      render: (price, item) => (
        <InputNumber
          size="small"
          value={price}
          min={0}
          precision={2}
          prefix="NPR"
          style={{ width: "100%" }}
          onChange={(v) =>
            updateCartItem(item.product_id, { unit_price: v || 0 })
          }
        />
      ),
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      width: 100,
      render: (val) => <Text strong>{formatMoney(val)}</Text>,
    },
    {
      title: "",
      key: "actions",
      width: 44,
      render: (_, item) => (
        <Button
          size="small"
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeFromCart(item.product_id)}
        />
      ),
    },
  ];

  const productInCart = (id) => cart.find((item) => item.product_id === id);

  return (
    <div className="pos-page">
      <Row gutter={16} style={{ marginBottom: 16 }} align="middle">
        <Col flex="auto">
          <Space>
            <Title level={4} style={{ margin: 0 }}>
              Point of Sale
            </Title>
            <Tag
              color={isOnline ? "green" : "red"}
              icon={
                isOnline ? <CheckCircleOutlined /> : <ShoppingCartOutlined />
              }
            >
              {isOnline ? "Online" : "Offline"}
            </Tag>
            {queuedSales.length > 0 && (
              <Badge count={queuedSales.length}>
                <Tag color="orange">Queued sales</Tag>
              </Badge>
            )}
          </Space>
        </Col>
        {queuedSales.length > 0 && (
          <Col>
            <Button
              icon={<SyncOutlined />}
              loading={syncing}
              onClick={handleSync}
            >
              Sync {queuedSales.length} sale(s)
            </Button>
          </Col>
        )}
      </Row>

      {!isOnline && (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          message="You are offline. Sales will be stored locally and synced when you reconnect."
        />
      )}

      <Row gutter={16}>
        {/* ---------- Catalog + scan ---------- */}
        <Col xs={24} lg={15} xl={16}>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Row gutter={8} align="middle">
              <Col span={12}>
                <Input
                  ref={scanInputRef}
                  size="large"
                  prefix={<ScanOutlined />}
                  placeholder="Scan barcode or enter code…"
                  value={scanInput}
                  onChange={(e) => setScanInput(e.target.value)}
                  onPressEnter={handleScanSubmit}
                  loading={scanning}
                  allowClear
                />
              </Col>
              <Col span={4}>
                <Button
                  size="large"
                  block
                  icon={<CameraOutlined />}
                  onClick={() => setCameraOpen(true)}
                >
                  Camera
                </Button>
              </Col>
              <Col span={8}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Wedge scanner works instantly — just scan and items are added.
                </Text>
              </Col>
            </Row>
          </Card>

          <Card size="small">
            <Row gutter={8} style={{ marginBottom: 12 }}>
              <Col span={14}>
                <Input
                  prefix={<SearchOutlined />}
                  placeholder="Search name, barcode or SKU…"
                  value={catalogSearch}
                  onChange={(e) => setCatalogSearch(e.target.value)}
                  allowClear
                />
              </Col>
              <Col span={10}>
                <Select
                  placeholder="All categories"
                  style={{ width: "100%" }}
                  value={categoryFilter}
                  onChange={setCategoryFilter}
                  allowClear
                >
                  {categories.map((c) => (
                    <Option key={c.id} value={c.id}>
                      {c.name}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>

            {productsLoading ? (
              <div style={{ textAlign: "center", padding: 40 }}>
                <Spin />
              </div>
            ) : filteredProducts.length === 0 ? (
              <Empty description="No products found" />
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                  gap: 10,
                  maxHeight: 560,
                  overflowY: "auto",
                }}
              >
                {filteredProducts.map((p) => {
                  const inCart = productInCart(p.id);
                  const outOfStock =
                    typeof p.stock_quantity === "number" &&
                    p.stock_quantity <= (inCart?.quantity || 0);
                  return (
                    <Tooltip
                      key={p.id}
                      title={`${p.name} — ${formatMoney(p.price)} ${
                        typeof p.stock_quantity === "number"
                          ? `| Stock: ${p.stock_quantity}`
                          : ""
                      }`}
                    >
                      <Button
                        block
                        disabled={outOfStock}
                        onClick={() => addToCart(p)}
                        style={{
                          height: "auto",
                          padding: "10px 12px",
                          textAlign: "left",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          gap: 2,
                          background: outOfStock ? "#fafafa" : "#ffffff",
                        }}
                      >
                        <Text strong style={{ width: "100%" }} ellipsis>
                          {p.name}
                        </Text>
                        <Text style={{ color: "#16a34a", fontWeight: 700 }}>
                          {formatMoney(p.price)}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {p.barcode || p.sku || ""}{" "}
                          {inCart ? `· ${inCart.quantity} in cart` : ""}
                        </Text>
                        {outOfStock && (
                          <Tag color="red" style={{ margin: 0 }}>
                            Out of stock
                          </Tag>
                        )}
                      </Button>
                    </Tooltip>
                  );
                })}
              </div>
            )}
          </Card>
        </Col>

        {/* ---------- Cart ---------- */}
        <Col xs={24} lg={9} xl={8}>
          <Card
            size="small"
            title={
              <Space>
                <ShoppingCartOutlined />
                Current Sale
                <Badge count={cart.length} showZero />
              </Space>
            }
          >
            {cart.length === 0 ? (
              <Empty description="Scan or tap items to start a sale" />
            ) : (
              <Table
                rowKey="product_id"
                size="small"
                columns={cartColumns}
                dataSource={cart}
                pagination={false}
                scroll={{ y: 300 }}
              />
            )}

            <Divider style={{ margin: "12px 0" }} />

            <Row gutter={[8, 8]}>
              <Col span={12}>
                <Text type="secondary">Subtotal</Text>
              </Col>
              <Col span={12} style={{ textAlign: "right" }}>
                <Text>{formatMoney(subtotal)}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">VAT ({vatRate}%)</Text>
              </Col>
              <Col span={12} style={{ textAlign: "right" }}>
                <Text>{formatMoney(vatAmount)}</Text>
              </Col>
              <Col span={24}>
                <Divider style={{ margin: "6px 0" }} />
              </Col>
              <Col span={12}>
                <Text strong style={{ fontSize: 18 }}>
                  Total
                </Text>
              </Col>
              <Col span={12} style={{ textAlign: "right" }}>
                <Text strong style={{ fontSize: 18, color: "#16a34a" }}>
                  {formatMoney(total)}
                </Text>
              </Col>
            </Row>

            <Divider style={{ margin: "12px 0" }}>Details</Divider>

            <Space direction="vertical" style={{ width: "100%" }} size={8}>
              <Select
                placeholder="Customer (optional — walk-in)"
                style={{ width: "100%" }}
                showSearch
                allowClear
                optionFilterProp="children"
                value={customerId}
                onChange={(v) => {
                  const customer = customers.find((c) => c.id === v);
                  setCheckoutField("customerId", v || null);
                  setCheckoutField(
                    "customerName",
                    customer
                      ? customer.name || customer.full_name || null
                      : null,
                  );
                  setCheckoutField(
                    "customerPhone",
                    customer ? customer.phone_number || null : null,
                  );
                  setCheckoutField(
                    "customerEmail",
                    customer ? customer.email || null : null,
                  );
                }}
              >
                {customers.map((c) => (
                  <Option key={c.id} value={c.id}>
                    {c.name || c.full_name}{" "}
                    {c.phone_number ? `(${c.phone_number})` : ""}
                  </Option>
                ))}
              </Select>

              <Row gutter={8}>
                <Col span={12}>
                  <Select
                    placeholder="Payment method"
                    style={{ width: "100%" }}
                    value={paymentMethod}
                    onChange={(v) => setCheckoutField("paymentMethod", v)}
                  >
                    <Option value="cash">Cash</Option>
                    <Option value="card">Card</Option>
                    <Option value="esewa">eSewa</Option>
                    <Option value="khalti">Khalti</Option>
                    <Option value="bank">Bank Transfer</Option>
                    <Option value="online">Online</Option>
                    <Option value="credit">Credit</Option>
                  </Select>
                </Col>
                <Col span={12}>
                  <Select
                    placeholder="Billing type"
                    style={{ width: "100%" }}
                    value={billingType}
                    onChange={(v) => setCheckoutField("billingType", v)}
                  >
                    <Option value="vat">VAT (products)</Option>
                    <Option value="vat_all">VAT (all)</Option>
                    <Option value="standard">No VAT</Option>
                  </Select>
                </Col>
              </Row>

              {isVat && (
                <Row gutter={8} align="middle">
                  <Col span={12}>
                    <InputNumber
                      value={vatRate}
                      min={0}
                      max={100}
                      precision={0}
                      addonAfter="%"
                      style={{ width: "100%" }}
                      onChange={(v) => setCheckoutField("vatRate", v ?? 0)}
                    />
                  </Col>
                </Row>
              )}

              {paymentMethod === "cash" && (
                <Row gutter={8} align="middle">
                  <Col span={12}>
                    <InputNumber
                      prefix={<MoneyCollectOutlined />}
                      placeholder="Cash received"
                      value={cashReceived}
                      min={0}
                      precision={2}
                      style={{ width: "100%" }}
                      onChange={setCashReceived}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Change"
                      value={change}
                      precision={2}
                      valueStyle={{ color: "#16a34a", fontSize: 16 }}
                    />
                  </Col>
                </Row>
              )}

              <Checkbox
                checked={billToo}
                onChange={(e) => setBillToo(e.target.checked)}
              >
                Generate invoice automatically
              </Checkbox>

              <Button
                type="primary"
                size="large"
                block
                icon={<ShoppingCartOutlined />}
                disabled={cart.length === 0}
                onClick={handleComplete}
              >
                Complete Sale — {formatMoney(total)}
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* ---------- Camera scanner modal ---------- */}
      <Modal
        title="Scan Barcode with Camera"
        open={cameraOpen}
        onCancel={() => setCameraOpen(false)}
        footer={null}
        width={420}
        forceRender
      >
        <div
          id="pos-camera-scanner"
          style={{ width: "100%", maxHeight: 360, overflow: "hidden" }}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          Point the camera at the product barcode. It will be added to the cart
          automatically.
        </Text>
      </Modal>
    </div>
  );
};

export default POS;
